import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Play, Flag } from "lucide-react";

const SIZE = 9, MINES = 10;

function build() {
  const cells = Array.from({ length: SIZE * SIZE }, () => ({ mine: false, open: false, flag: false, n: 0 }));
  let placed = 0;
  while (placed < MINES) {
    const i = Math.floor(Math.random() * SIZE * SIZE);
    if (!cells[i].mine) { cells[i].mine = true; placed++; }
  }
  const idx = (r, c) => r * SIZE + c;
  for (let r = 0; r < SIZE; r++) for (let c = 0; c < SIZE; c++) {
    if (cells[idx(r, c)].mine) continue;
    let n = 0;
    for (let dr = -1; dr <= 1; dr++) for (let dc = -1; dc <= 1; dc++) {
      const nr = r + dr, nc = c + dc;
      if (nr >= 0 && nr < SIZE && nc >= 0 && nc < SIZE && cells[idx(nr, nc)].mine) n++;
    }
    cells[idx(r, c)].n = n;
  }
  return cells;
}

export function Minesweeper({ onScore }) {
  const [cells, setCells] = useState(build);
  const [running, setRunning] = useState(false);
  const [over, setOver] = useState(false);
  const [won, setWon] = useState(false);
  const [flagMode, setFlagMode] = useState(false);
  const [time, setTime] = useState(0);
  const reported = useRef(false);

  useEffect(() => { if (!running) return; const id = setInterval(() => setTime((t) => t + 1), 1000); return () => clearInterval(id); }, [running]);

  const start = () => { setCells(build()); setRunning(true); setOver(false); setWon(false); setTime(0); reported.current = false; };

  const flood = (cs, i) => {
    const stack = [i];
    while (stack.length) {
      const cur = stack.pop();
      const cell = cs[cur];
      if (cell.open || cell.flag) continue;
      cell.open = true;
      if (cell.n === 0 && !cell.mine) {
        const r = Math.floor(cur / SIZE), c = cur % SIZE;
        for (let dr = -1; dr <= 1; dr++) for (let dc = -1; dc <= 1; dc++) {
          const nr = r + dr, nc = c + dc;
          if (nr >= 0 && nr < SIZE && nc >= 0 && nc < SIZE) stack.push(nr * SIZE + nc);
        }
      }
    }
  };

  const click = (i) => {
    if (!running) return;
    const cs = cells.map((c) => ({ ...c }));
    if (flagMode) { cs[i].flag = !cs[i].flag; setCells(cs); return; }
    if (cs[i].flag) return;
    if (cs[i].mine) {
      cs.forEach((c) => { if (c.mine) c.open = true; });
      setCells(cs); setRunning(false); setOver(true); return;
    }
    flood(cs, i);
    setCells(cs);
    const safe = cs.filter((c) => !c.mine);
    if (safe.every((c) => c.open)) { setRunning(false); setWon(true); }
  };

  useEffect(() => {
    if ((won || over) && !reported.current) { reported.current = true; onScore?.(won ? Math.max(100, 1500 - time * 10) : 0); }
  }, [won, over, time, onScore]);

  const flags = cells.filter((c) => c.flag).length;

  return (
    <div className="flex flex-col items-center" data-testid="minesweeper">
      <div className="flex items-center gap-4 mb-3 font-mono text-sm">
        <span className="text-neon-pink">💣 {MINES - flags}</span>
        <span className="text-neon-yellow">⏱ {time}s</span>
        <button onClick={() => setFlagMode(!flagMode)} data-testid="mine-flag-toggle"
          aria-pressed={flagMode}
          className={`px-3 py-1 rounded text-xs font-bold uppercase border-2 ${flagMode ? "border-neon-pink text-neon-pink" : "border-zinc-700 text-zinc-400"}`}>
          <Flag className="w-3 h-3 inline mr-1" /> {flagMode ? "Flagging" : "Flag"}
        </button>
      </div>
      <div className="relative grid gap-0.5 p-1 bg-zinc-900 border-2 border-zinc-700" style={{ gridTemplateColumns: `repeat(${SIZE}, 1fr)`, width: "min(92vw, 360px)" }}>
        {cells.map((cell, i) => (
          <button key={i} onClick={() => click(i)} data-testid={`mine-cell-${i}`}
            className="aspect-square flex items-center justify-center font-bold text-sm"
            style={{
              background: cell.open ? (cell.mine ? "#FF003C" : "#18181b") : "#3f3f46",
              color: ["", "#00F0FF", "#39FF14", "#EFFF04", "#FF6B00", "#FF003C", "#a855f7", "#fff", "#fff"][cell.n],
            }}>
            {cell.open ? (cell.mine ? "💣" : cell.n || "") : cell.flag ? "🚩" : ""}
          </button>
        ))}
        {!running && (
          <div className="absolute inset-0 bg-black/85 flex flex-col items-center justify-center gap-3">
            {won && <p className="font-display font-black text-2xl text-neon-lime uppercase" data-testid="mine-won">Cleared! {time}s</p>}
            {over && <p className="font-display font-black text-2xl text-neon-pink uppercase" data-testid="mine-over">Boom! 💥</p>}
            <Button onClick={start} data-testid="mine-start" className="bg-neon-cyan text-black rounded-none font-bold uppercase">
              <Play className="w-4 h-4 mr-1 fill-black" /> {won || over ? "Play Again" : "Start"}
            </Button>
          </div>
        )}
      </div>
      <p className="text-zinc-500 text-xs font-mono mt-3">Open safe cells. Toggle Flag to mark mines.</p>
    </div>
  );
}
